import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import block from 'bem-cn-lite';
import {compose} from 'recompose';
import {TextInput, Button, Spin, YCSelect} from '@yandex-data-ui/common';
import _ from 'lodash';
import {HTML} from '@doc-tools/components';

import {formatPrice, getConsoleLink, formatName, getConsoleSpecialLink} from 'utils';

import withEnv from 'hoc/withEnv';
import withLocale from 'hoc/withLocale';
import withTranslation from 'hoc/withTranslation';

import DelayRender from 'components/DelayRender/DelayRender';
import CloseButton from 'components/Buttons/CloseButton/CloseButton';
import {CALCULALTOR_QA_PREFIX} from '../PricesCalculator/PricesCalculator';

import './Receipt.scss';

const b = block('Receipt');
const MULTIPLICATION_SIGN = '×';

class Receipt extends React.Component {
    static propTypes = {
        locale: PropTypes.object.isRequired,
        env: PropTypes.string.isRequired,
        title: PropTypes.string,
        items: PropTypes.arrayOf(
            PropTypes.shape({
                ...Receipt.itemPropTypes,
                id: PropTypes.string.isRequired,
                multi: PropTypes.boolean,
                items: PropTypes.arrayOf(PropTypes.shape(Receipt.itemPropTypes)),
            }),
        ),
        period: PropTypes.oneOf(['day', 'month']),
        isLoading: PropTypes.bool,
        isOneService: PropTypes.bool,
        error: PropTypes.object,
        onPeriodChange: PropTypes.func,
        onItemClose: PropTypes.func,
        onErrorActionClick: PropTypes.func,
        customOsProductId: PropTypes.string,
        i18n: PropTypes.func,
        i18nK: PropTypes.func,
    };

    static defaultProps = {
        items: [],
        period: 'month',
        isLoading: false,
        onPeriodChange: _.noop,
        onItemClose: _.noop,
        onErrorActionClick: _.noop,
    };

    constructor(props) {
        super(props);

        this.state = {
            quantityValues: this.prepareQuantityValues(this.props),
        };
    }

    componentDidUpdate(prevProps) {
        if (!_.isEqual(this.props.items, prevProps.items)) {
            this.setState((prevState) => ({
                quantityValues: this.prepareQuantityValues(this.props, prevState),
            }));
        }
    }

    static itemPropTypes = {
        name: PropTypes.string.isRequired,
        cost: PropTypes.number.isRequired,
        nonClosable: PropTypes.bool,
    };

    getConsoleLink() {
        const {
            locale: {tld},
            env,
            customOsProductId,
        } = this.props;

        const domain = getConsoleLink(tld, env);

        if (customOsProductId) {
            return getConsoleSpecialLink(tld, env, '/compute/create-instance', {
                productId: customOsProductId,
            });
        }

        return domain;
    }

    prepareQuantityValues(props, state) {
        return props.items.reduce((acc, item) => {
            if (item.multi) {
                if (state && typeof state.quantityValues[item.id] === 'number') {
                    acc[item.id] = state.quantityValues[item.id];
                } else {
                    acc[item.id] = 1;
                }
            }

            return acc;
        }, {});
    }

    formatCost(value) {
        const {locale} = this.props;
        return formatPrice(value, locale, {maxPrecision: 4});
    }

    getTotal() {
        const {items} = this.props;
        const {quantityValues} = this.state;

        return items.reduce(
            (acc, {id, multi, cost}) => acc + (multi ? quantityValues[id] * cost : cost),
            0,
        );
    }

    handlePeriodChange = (value) => {
        const {onPeriodChange} = this.props;

        onPeriodChange(value);
    };

    handleQuantityChange = (id, value) => {
        const regexp = /^[0-9]*$/;

        if (!regexp.test(value) || value.length > 3) {
            return;
        }

        this.setState((prevState) => ({
            quantityValues: {
                ...prevState,
                [id]: Number(value),
            },
        }));
    };

    renderLoader() {
        const {isLoading} = this.props;

        if (!isLoading) {
            return null;
        }

        return (
            <DelayRender>
                {() => (
                    <div className={b('loader')}>
                        <Spin size="s" />
                    </div>
                )}
            </DelayRender>
        );
    }

    renderError() {
        const {onErrorActionClick, i18nK} = this.props;

        return (
            <div className={b('error')}>
                <div className={b('error-msg')}>{i18nK('label_error')}</div>
                <div className={b('error-action')}>
                    <Button onClick={onErrorActionClick}>{i18nK('button_try-again')}</Button>
                </div>
            </div>
        );
    }

    renderContent() {
        const {items, error} = this.props;

        if (error) {
            return this.renderError();
        }

        return this.renderPrimaryItems(items);
    }

    renderPrimaryItems(items) {
        const {onItemClose, isOneService} = this.props;
        const {quantityValues} = this.state;

        return (
            <ul className={b('items')}>
                {items.map(({id, name, cost, multi, items: subItems, nonClosable}) => (
                    <li key={id} className={b('item')}>
                        <div className={b('item-row', {type: 'primary'})}>
                            {!isOneService && !nonClosable && (
                                <CloseButton
                                    className={b('item-close')}
                                    onClick={() => onItemClose(id)}
                                />
                            )}
                            <div className={b('item-name')}>
                                <span className={b('item-name-text')}>
                                    <HTML>{name}</HTML>
                                </span>
                                {multi && (
                                    <span className={b('item-quantity')}>
                                        <span className={b('item-quantity-sign')}>
                                            {MULTIPLICATION_SIGN}
                                        </span>
                                        <span className={b('item-quantity-control')}>
                                            <TextInput
                                                size="s"
                                                type="number"
                                                controlProps={{pattern: '[0-9]*'}}
                                                value={String(quantityValues[id])}
                                                onUpdate={(value) =>
                                                    this.handleQuantityChange(id, value)
                                                }
                                            />
                                        </span>
                                    </span>
                                )}
                            </div>
                            <div className={b('item-cost')}>
                                {this.formatCost(multi ? quantityValues[id] * cost : cost)}
                            </div>
                        </div>
                        {subItems && this.renderSecondaryItems(subItems, id, multi)}
                    </li>
                ))}
            </ul>
        );
    }

    renderSecondaryItems(items, id, multi) {
        const {quantityValues} = this.state;

        return (
            <ul className={b('items')}>
                {items.map(({name, cost}, index) => {
                    return (
                        <li key={index} className={b('item')}>
                            <div className={b('item-row', {type: 'secondary'})}>
                                <div className={b('item-name')}>{formatName(name)}</div>
                                <div className={b('item-cost')}>
                                    {this.formatCost(multi ? quantityValues[id] * cost : cost)}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        );
    }

    render() {
        const {
            locale: {lang},
            title,
            period,
            error,
            isOneService,
            i18nK,
        } = this.props;

        return (
            <div className={b({'one-service': isOneService})}>
                {this.renderLoader()}
                <div className={b('title')}>{title}</div>
                <div className={b('content')}>{this.renderContent()}</div>
                <div className={b('bottom')}>
                    {!error && (
                        <div className={b('total')}>
                            <div
                                className={b('total-title')}
                                data-qa={`${CALCULALTOR_QA_PREFIX}-total`}
                            >
                                {this.formatCost(this.getTotal())}
                            </div>
                            <YCSelect
                                size="m"
                                value={period}
                                className={b('total-period')}
                                onUpdate={this.handlePeriodChange}
                                showSearch={false}
                                items={[
                                    {value: 'day', title: i18nK('value_per-day')},
                                    {value: 'month', title: i18nK('value_per-month')},
                                ]}
                            ></YCSelect>
                        </div>
                    )}
                    <Button
                        size="l"
                        view="action"
                        href={this.getConsoleLink(lang)}
                        className={b('action')}
                    >
                        {i18nK('button_receipt-action')}
                    </Button>
                    {isOneService && (
                        <Fragment>
                            <Button size="l" view="outlined" href="/prices" className={b('link')}>
                                {i18nK('button_to-main-calculator')}
                            </Button>
                            <div className={b('note')}>
                                <HTML>{i18nK('label_receipt_note')}</HTML>
                            </div>
                        </Fragment>
                    )}
                </div>
            </div>
        );
    }
}

export default compose(withEnv, withLocale, withTranslation('calculator'))(Receipt);
